import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  Business,
  Star,
  Security,
  Speed,
  TrendingUp,
  ContactMail,
} from '@mui/icons-material';

const Home = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Star sx={{ fontSize: 48 }} />,
      title: 'Professional Excellence',
      description: 'Over 20 years of experience in estate sales and liquidation services across Florida.',
    },
    {
      icon: <Security sx={{ fontSize: 48 }} />,
      title: 'Trust & Integrity',
      description: 'Fully licensed, bonded, and insured for your peace of mind.',
    },
    {
      icon: <Speed sx={{ fontSize: 48 }} />,
      title: 'Fast Turnaround',
      description: 'Quick and efficient sales process from start to finish.',
    },
    {
      icon: <TrendingUp sx={{ fontSize: 48 }} />,
      title: 'Maximum Value',
      description: 'Expert appraisal and pricing to maximize your returns.',
    },
  ];

  return (
    <Box>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <Business sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            PaceEstate
          </Typography>
          <Button color="inherit" onClick={() => navigate('/contact')}>
            Contact
          </Button>
          <Button color="inherit" onClick={() => navigate('/login')}>
            Login
          </Button>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          background: 'linear-gradient(135deg, #003366 0%, #1a4d7a 100%)',
          color: 'white',
          py: 12,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h2" component="h1" gutterBottom fontWeight="bold">
            Professional Estate Sales
          </Typography>
          <Typography variant="h5" gutterBottom sx={{ mb: 4, opacity: 0.9 }}>
            Serving Florida with Excellence Since 2003
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.8 }}>
            Full-service estate liquidation, downsizing, and property management solutions
          </Typography>
          <Box>
            <Button
              variant="contained"
              size="large"
              color="secondary"
              onClick={() => navigate('/contact')}
              sx={{ mr: 2, px: 4, py: 1.5 }}
              startIcon={<ContactMail />}
            >
              Get Free Consultation
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/login')}
              sx={{
                color: 'white',
                borderColor: 'white',
                px: 4,
                py: 1.5,
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              Client Login
            </Button>
          </Box>
        </Container>
      </Box>

      <Container sx={{ py: 8 }}>
        <Typography variant="h3" align="center" gutterBottom fontWeight="bold" color="primary">
          Why Choose PaceEstate?
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
          Professional estate sales management you can trust
        </Typography>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                <CardContent>
                  <Box sx={{ color: 'secondary.main', mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box sx={{ backgroundColor: 'background.default', py: 8 }}>
        <Container>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" gutterBottom fontWeight="bold" color="primary">
                Our Services
              </Typography>
              <Typography variant="h6" color="text.secondary" paragraph>
                Comprehensive Estate Solutions
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                From full estate sales to downsizing assistance, we provide complete support
                throughout the entire process. Our experienced team handles everything from
                appraisal and pricing to marketing, sale execution, and cleanup.
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                We specialize in estate liquidation, downsizing services, property cleanouts,
                antique appraisals, and consignment sales. Our goal is to maximize value while
                minimizing stress during difficult transitions.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/contact')}
                sx={{ mt: 2 }}
              >
                Learn More
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 4, backgroundColor: 'primary.main', color: 'white' }}>
                <Typography variant="h5" gutterBottom fontWeight="bold">
                  Areas We Serve
                </Typography>
                <Typography variant="body1" paragraph>
                  Proudly serving communities throughout Florida including:
                </Typography>
                <Typography variant="body1" component="div">
                  <ul style={{ paddingLeft: 20 }}>
                    <li>Tallahassee & Leon County</li>
                    <li>Pensacola & Escambia County</li>
                    <li>Panama City & Bay County</li>
                    <li>Jacksonville & Duval County</li>
                    <li>And surrounding areas</li>
                  </ul>
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: 8, textAlign: 'center', backgroundColor: 'white' }}>
        <Container maxWidth="md">
          <Typography variant="h3" gutterBottom fontWeight="bold" color="primary">
            Ready to Get Started?
          </Typography>
          <Typography variant="h6" color="text.secondary" paragraph>
            Contact us today for a free, no-obligation consultation
          </Typography>
          <Button
            variant="contained"
            size="large"
            color="secondary"
            onClick={() => navigate('/contact')}
            sx={{ px: 6, py: 2, fontSize: '1.1rem' }}
          >
            Request Free Consultation
          </Button>
        </Container>
      </Box>

      <Box sx={{ py: 3, backgroundColor: 'primary.main', color: 'white' }}>
        <Container>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="body2">
                © 2024 PaceEstate. All rights reserved.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6} textAlign={{ xs: 'left', md: 'right' }}>
              <Typography variant="body2">
                Licensed & Insured | Serving Florida Since 2003
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
